import type { Gateway, GatewayEndpoint } from "./types";

/**
 * Gateway ADDRESSES — the pure rules behind "one broker card, several gateway instances".
 *
 * A broker's stack may run several gateway instances (vybe-1.gw…, vybe-2.gw…). The gateway says so
 * on `GET /api/meta` with `endpointsAreGateways: true`; a server's `endpoints[]` are then HUB
 * addresses the terminal dials directly, not upstream broker addresses relayed through the card's
 * one hub (the MT5 shape, which stays exactly as it was when the flag is absent or false).
 *
 * Everything here is pure (no fetch, no store) so the choice a trader's account depends on is
 * covered by unit tests; the network and React sides stay thin wrappers.
 */

/** What a gateway reports about itself that the address picker needs (`GET {hub}/api/meta`). */
export interface GatewayMetaInfo {
  /** True when a server's `endpoints[]` are gateway (hub) addresses. Absent on the wire = false. */
  endpointsAreGateways: boolean;
  /** The full ordered address list, identical on every instance of the stack. */
  endpoints: GatewayEndpoint[];
  /** Which instance of the stack answered (1..N), when it says. */
  instance: number | null;
}

export const NO_GATEWAY_META: GatewayMetaInfo = {
  endpointsAreGateways: false,
  endpoints: [],
  instance: null,
};

const isEndpoint = (e: unknown): e is GatewayEndpoint => {
  if (!e || typeof e !== "object") return false;
  const { host, port } = e as Record<string, unknown>;
  return (
    typeof host === "string" &&
    host.trim().length > 0 &&
    typeof port === "number" &&
    Number.isInteger(port) &&
    port > 0 &&
    port <= 65535
  );
};

/** Read the address fields off a `/api/meta` body. Anything missing or malformed reads as "not gateways". */
export function parseGatewayMeta(body: unknown): GatewayMetaInfo {
  if (!body || typeof body !== "object") return NO_GATEWAY_META;
  const raw = body as Record<string, unknown>;
  const endpoints = Array.isArray(raw.endpoints) ? raw.endpoints.filter(isEndpoint) : [];
  return {
    // Strictly `true`: a truthy string or 1 from some other gateway must not flip the MT5 flow.
    endpointsAreGateways: raw.endpointsAreGateways === true && endpoints.length > 0,
    endpoints,
    instance:
      typeof raw.instance === "number" && Number.isInteger(raw.instance) ? raw.instance : null,
  };
}

/** The http(s) scheme a hub URL is served over (`wss` → `https`, `ws` → `http`). Unknown → https. */
export function hubScheme(hubUrl: string | null | undefined): "http" | "https" {
  return /^(http|ws):\/\//i.test((hubUrl ?? "").trim()) ? "http" : "https";
}

const DEFAULT_PORT = { http: 80, https: 443 } as const;

/**
 * The hub URL of one gateway address: `https://<host>`, with `:port` only when it is not the
 * scheme's default (443). `scheme` follows the card's own hub URL so a plain-http dev stack keeps
 * working; every production card is https.
 */
export function buildHubUrl(
  endpoint: GatewayEndpoint,
  scheme: "http" | "https" = "https"
): string {
  const host = endpoint.host.trim();
  return endpoint.port === DEFAULT_PORT[scheme]
    ? `${scheme}://${host}`
    : `${scheme}://${host}:${endpoint.port}`;
}

/**
 * One comparable form for a hub URL: ws(s) → http(s), lower-case host, default port dropped, no
 * path, no trailing slash. Two spellings of the same address compare equal; unparseable text
 * compares as its trimmed lower-case self.
 */
export function normalizeHubUrl(url: string | null | undefined): string {
  const text = (url ?? "").trim();
  if (!text) return "";
  try {
    const u = new URL(
      text.replace(/^wss:\/\//i, "https://").replace(/^ws:\/\//i, "http://")
    );
    return `${u.protocol}//${u.host}`.toLowerCase();
  } catch {
    return text.replace(/\/+$/, "").toLowerCase();
  }
}

export const sameHubUrl = (a?: string | null, b?: string | null): boolean => {
  const na = normalizeHubUrl(a);
  return na !== "" && na === normalizeHubUrl(b);
};

/** Stable key for an endpoint (host is case-insensitive). */
export const endpointKey = (e: GatewayEndpoint): string =>
  `${e.host.trim().toLowerCase()}:${e.port}`;

/** De-duplicate endpoints keeping the first occurrence and the advertised order. */
export function uniqueEndpoints(endpoints: readonly GatewayEndpoint[]): GatewayEndpoint[] {
  const seen = new Set<string>();
  const out: GatewayEndpoint[] = [];
  for (const e of endpoints) {
    const key = endpointKey(e);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

/** Index of the endpoint an account is saved against, or -1. */
export function findEndpointIndex(
  endpoints: readonly GatewayEndpoint[],
  host: string | null | undefined,
  port: number | null | undefined
): number {
  if (!host) return -1;
  const key = `${host.trim().toLowerCase()}:${port ?? 443}`;
  return endpoints.findIndex((e) => endpointKey(e) === key);
}

/** Live state of one gateway address, from its own `/healthz`. */
export type AddressState = "checking" | "ready" | "not-ready" | "unreachable";

export interface AddressHealth {
  state: AddressState;
  /** Measured round trip of the health request, in ms. Absent while checking / when unreachable. */
  latencyMs?: number;
}

export const ADDRESS_STATE_LABEL: Record<AddressState, string> = {
  checking: "checking…",
  ready: "ready",
  "not-ready": "not ready",
  unreachable: "unreachable",
};

/**
 * Classify what `/healthz` returned. `null` = the request failed or timed out (unreachable). An
 * answer is "ready" only when it is a 2xx whose JSON body says `ready: true`; any other answer —
 * an error status, a non-JSON page, `ready: false` — proves the host is up but not serving.
 */
export function classifyHealth(
  answer: { ok: boolean; body: unknown } | null
): Exclude<AddressState, "checking"> {
  if (!answer) return "unreachable";
  const body = answer.body as { ready?: unknown } | null | undefined;
  return answer.ok && typeof body === "object" && body !== null && body.ready === true
    ? "ready"
    : "not-ready";
}

/**
 * Which round trip to show for an address that was measured twice in a row. The first request pays
 * for the DNS lookup and the TLS handshake; the second reuses the connection, so it is the honest
 * figure and the one reported. Without a usable second sample (that request failed or timed out)
 * the first one stands.
 */
export function reportedLatencyMs(firstMs: number, secondMs: number | null | undefined): number {
  return typeof secondMs === "number" && Number.isFinite(secondMs) && secondMs >= 0
    ? secondMs
    : firstMs;
}

/** What one address row says: `host:port`, its state in words, and the round trip when measured. */
export interface AddressSummary {
  address: string;
  state: AddressState;
  stateLabel: string;
  /** e.g. `"49 ms"`; null while checking / when unreachable. */
  latencyLabel: string | null;
}

/** The text of one address row. No health yet reads as "checking…". */
export function describeAddress(
  endpoint: GatewayEndpoint,
  health: AddressHealth | null | undefined
): AddressSummary {
  const state: AddressState = health?.state ?? "checking";
  return {
    address: `${endpoint.host}:${endpoint.port}`,
    state,
    stateLabel: ADDRESS_STATE_LABEL[state],
    latencyLabel: health?.latencyMs !== undefined ? `${health.latencyMs} ms` : null,
  };
}

/**
 * How the compact "Gateway address" field behaves. One address that is already the chosen one is
 * plain text — there is nothing to choose. Several addresses, or one that is not the chosen one yet
 * (an account saved against an address the broker no longer lists), open a selector. No addresses:
 * no field.
 */
export function addressFieldMode(
  count: number,
  selectedIndex: number
): "hidden" | "read-only" | "select" {
  if (count <= 0) return "hidden";
  return count === 1 && selectedIndex === 0 ? "read-only" : "select";
}

/**
 * Keyboard movement inside the address selector: ↑/↓ step and wrap, Home/End jump. Any other key —
 * or an empty list — leaves the focus where it is (returns `current`).
 */
export function nextAddressIndex(current: number, count: number, key: string): number {
  if (count <= 0) return current;
  switch (key) {
    case "ArrowDown":
      return current < 0 ? 0 : (current + 1) % count;
    case "ArrowUp":
      return current <= 0 ? count - 1 : current - 1;
    case "Home":
      return 0;
    case "End":
      return count - 1;
    default:
      return current;
  }
}

/**
 * Which address to preselect. Ready addresses first, the lowest round trip among them; equal round
 * trips (and addresses with no measurement) fall back to the advertised order. With nothing ready,
 * an address that at least answered beats one that did not, by the same rule. With nothing
 * reachable at all — or nothing measured yet — the advertised order stands (index 0) and the trader
 * chooses.
 */
export function chooseBestAddress(
  endpoints: readonly GatewayEndpoint[],
  health: Readonly<Record<string, AddressHealth | undefined>>
): number {
  for (const wanted of ["ready", "not-ready"] as const) {
    let best = -1;
    let bestMs = Number.POSITIVE_INFINITY;
    endpoints.forEach((e, i) => {
      const h = health[endpointKey(e)];
      if (h?.state !== wanted) return;
      const ms = h.latencyMs ?? Number.POSITIVE_INFINITY;
      // Strictly lower wins, so the earlier address keeps a tie.
      if (best === -1 || ms < bestMs) {
        best = i;
        bestMs = ms;
      }
    });
    if (best !== -1) return best;
  }
  return 0;
}

/** True once every address has an answer (or a failure) — the moment the preselection is final. */
export function allProbed(
  endpoints: readonly GatewayEndpoint[],
  health: Readonly<Record<string, AddressHealth | undefined>>
): boolean {
  return endpoints.every((e) => {
    const state = health[endpointKey(e)]?.state;
    return state !== undefined && state !== "checking";
  });
}

/**
 * Every hub address the CRM says a card lives at: its `hubUrl`, each of its `hubUrls`, and each
 * advertised server row as a gateway address (`https://host[:port]` — for a multi-gateway broker the
 * CRM's flat server list is one row per gateway address). Normalised for comparison.
 */
export function gatewayHubAddresses(gateway: Gateway): Set<string> {
  const out = new Set<string>();
  const add = (url?: string | null) => {
    const n = normalizeHubUrl(url);
    if (n) out.add(n);
  };
  add(gateway.hubUrl);
  for (const url of gateway.hubUrls ?? []) add(url);
  const scheme = hubScheme(gateway.hubUrl);
  for (const s of gateway.servers ?? []) {
    if (isEndpoint(s)) add(buildHubUrl(s, scheme));
  }
  return out;
}

/**
 * The card an account's saved hub belongs to, if the CRM lists that hub under any card. An account
 * on a listed address is HEALTHY — it sits on one of the broker's own gateways and must never be
 * "repaired" onto a different address, whichever one the CRM happens to report as the card's
 * `hubUrl` right now.
 */
export function findGatewayByHubUrl(
  gateways: readonly Gateway[],
  hubUrl: string | null | undefined
): Gateway | undefined {
  const needle = normalizeHubUrl(hubUrl);
  if (!needle) return undefined;
  return gateways.find((g) => gatewayHubAddresses(g).has(needle));
}

export const isKnownGatewayAddress = (
  gateways: readonly Gateway[],
  hubUrl: string | null | undefined
): boolean => findGatewayByHubUrl(gateways, hubUrl) !== undefined;

/**
 * Where a new (or re-pointed) account lives. When the gateway's endpoints ARE gateways the account
 * dials the chosen address itself (`hubUrl = https://<host>[:port]`); otherwise the endpoint is an
 * upstream broker address relayed through the card's one hub, exactly as before. Host/port are the
 * chosen endpoint and the slug is the card's in both cases.
 */
export function accountHome(
  gateway: Pick<Gateway, "slug" | "hubUrl">,
  endpoint: GatewayEndpoint,
  endpointsAreGateways: boolean
): { gatewaySlug: string; hubUrl: string; host: string; port: number } {
  return {
    gatewaySlug: gateway.slug,
    hubUrl: endpointsAreGateways
      ? buildHubUrl(endpoint, hubScheme(gateway.hubUrl))
      : gateway.hubUrl,
    host: endpoint.host,
    port: endpoint.port,
  };
}
